import { AXLE_ROLES_BY_SUBJECT_KIND, type TyreLayoutAxleRole, type TyreLayoutSubjectKind } from '@chukua/common';
import { Alert, Button, Segmented, Select } from 'antd';
import clsx from 'clsx';
import { Plus, Trash2 } from 'lucide-react';
import { ARRANGEMENT_PRESETS, matchingPresetId, nextAxle, tyreChoices, type ArrangedAxle, type TyresPerAxle } from '@chukua/vehicle-model';
import { AXLE_ROLE_LABELS } from '@chukua/vehicle-model';
import type { TyreDiagramAxle } from '../public/TyrePositionDiagram';

const TYRE_LABELS: Record<TyresPerAxle, string> = { 2: 'Single', 4: 'Dual', 8: '4 a side' };
const MAX_AXLES = 8;
const CUSTOM = 'custom';

interface AxleArrangementEditorProps {
  kind: TyreLayoutSubjectKind;
  axles: ArrangedAxle[];
  onChange: (axles: ArrangedAxle[]) => void;
  /** The positions each axle has, as shown: generated from the arrangement, or the saved ones while it is unchanged. */
  positions: TyreDiagramAxle[];
  retiredCodes: string[];
  selectedCode: string | null;
  onSelect: (code: string) => void;
}

function PositionChips({
  positions,
  selectedCode,
  onSelect,
}: {
  positions: TyreDiagramAxle['positions'];
  selectedCode: string | null;
  onSelect: (code: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Tyre positions">
      {positions.map((position) => (
        <button
          key={position.code}
          type="button"
          title={position.name}
          aria-pressed={position.code === selectedCode}
          onClick={() => onSelect(position.code)}
          className={clsx(
            'cursor-pointer rounded border px-1.5 py-0.5 font-mono text-xs',
            position.code === selectedCode ? 'border-primary bg-primary text-white' : 'border-line bg-page text-fg hover:border-primary',
          )}
        >
          {position.code}
        </button>
      ))}
    </div>
  );
}

interface AxleRowProps {
  kind: TyreLayoutSubjectKind;
  index: number;
  axle: ArrangedAxle;
  isOnly: boolean;
  positions: TyreDiagramAxle['positions'];
  selectedCode: string | null;
  onSelect: (code: string) => void;
  onChange: (change: Partial<ArrangedAxle>) => void;
  onRemove: () => void;
}

function AxleRow({ kind, index, axle, isOnly, positions, selectedCode, onSelect, onChange, onRemove }: AxleRowProps) {
  const label = `Axle ${index + 1}`;
  return (
    <li className="flex flex-col gap-2 rounded-md border border-line p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="w-14 text-sm font-medium text-fg">{label}</span>
        <Select<TyreLayoutAxleRole>
          size="small"
          aria-label={`${label} role`}
          className="w-28"
          value={axle.role}
          options={AXLE_ROLES_BY_SUBJECT_KIND[kind].map((role) => ({ value: role, label: AXLE_ROLE_LABELS[role] }))}
          onChange={(role) => onChange({ role })}
        />
        <Segmented<TyresPerAxle>
          size="small"
          aria-label={`${label} tyres`}
          value={axle.tyres}
          options={tyreChoices(kind).map((tyres) => ({ value: tyres, label: TYRE_LABELS[tyres] }))}
          onChange={(tyres) => onChange({ tyres })}
        />
        <Button
          className="ml-auto"
          size="small"
          type="text"
          danger
          aria-label={`Remove ${label.toLowerCase()}`}
          icon={<Trash2 className="size-3.5" />}
          disabled={isOnly}
          onClick={onRemove}
        />
      </div>
      <PositionChips positions={positions} selectedCode={selectedCode} onSelect={onSelect} />
    </li>
  );
}

/**
 * The axles, front first, each a role and how many tyres it carries. The
 * positions on it are named for you, so every class and type reads the same
 * way. A standard arrangement fills the whole list at once.
 */
export function AxleArrangementEditor({ kind, axles, onChange, positions, retiredCodes, selectedCode, onSelect }: AxleArrangementEditorProps) {
  const presetId = matchingPresetId(kind, axles) ?? CUSTOM;
  const presetOptions = [
    ...ARRANGEMENT_PRESETS[kind].map((preset) => ({ value: preset.id, label: preset.label })),
    { value: CUSTOM, label: 'Custom', disabled: true },
  ];
  const update = (index: number, change: Partial<ArrangedAxle>) => onChange(axles.map((axle, i) => (i === index ? { ...axle, ...change } : axle)));

  return (
    <section aria-labelledby="axle-arrangement" className="flex flex-col gap-3 rounded-lg border border-line bg-surface p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="axle-arrangement" className="m-0 text-base font-semibold text-fg">
          Axles
        </h2>
        <Select
          aria-label="Standard arrangement"
          className="min-w-44"
          value={presetId}
          options={presetOptions}
          onChange={(id) => onChange(ARRANGEMENT_PRESETS[kind].find((preset) => preset.id === id)?.axles ?? axles)}
        />
      </div>
      <ol className="m-0 flex list-none flex-col gap-2 p-0">
        {axles.map((axle, index) => (
          <AxleRow
            key={index}
            kind={kind}
            index={index}
            axle={axle}
            isOnly={axles.length === 1}
            positions={positions[index]?.positions ?? []}
            selectedCode={selectedCode}
            onSelect={onSelect}
            onChange={(change) => update(index, change)}
            onRemove={() => onChange(axles.filter((_, i) => i !== index))}
          />
        ))}
      </ol>
      <Button
        type="dashed"
        icon={<Plus className="size-3.5" />}
        disabled={axles.length >= MAX_AXLES}
        onClick={() => onChange([...axles, nextAxle(kind, axles)])}
        block
      >
        Add axle
      </Button>
      {retiredCodes.length > 0 && (
        <Alert
          type="warning"
          showIcon
          title="Positions no longer on the vehicle are kept"
          description={`${retiredCodes.join(', ')} will be retired, not deleted, so old tyre records can still say where a tyre was fitted.`}
        />
      )}
    </section>
  );
}
